import React, { createContext, useContext, useState, useEffect } from "react";

// Define the User type
interface User {
    client_id: string | null;
    client_tenant_id: string | null;
    tenant_id: string;
    tenant_user_id: string;
    first_name: string;
    last_name: string;
    email_address: string;
    image: string;
    is_client: any;
    is_tenant: any;
}

// Define the context type
interface UserContextType {
    user: User | null;
    updateUser: (user: User | null) => void;
    fullname: string | null;
    image: string | null;
    type: string | null
}

// Create context with initial type
const UserContext = createContext<UserContextType | undefined>(undefined);

// Create custom hook for using the context
export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

// Define props type for the provider
interface UserProviderProps {
    children: React.ReactNode;
}

// Create the provider component
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [type, setType] = useState<string | null>(null);
    const [fullname, setFullname] = useState<string>('');
    const [image, setImage] = useState<string | null>(null)

    const updateUser = (newUser: User | null) => {
        setUser(newUser);
        localStorage.setItem('user', JSON.stringify(newUser));

        if(newUser?.is_client === 1){
            setType('client')
        }else{
            setType('tenant')
        }
    
        if (newUser) {
            const fullName = `${newUser.first_name} ${newUser.last_name}`;
            setFullname(fullName);
            setImage(newUser.image);
            localStorage.setItem('fullname', fullName);  // Store fullname in localStorage
            localStorage.setItem('userImage', newUser.image);
        } else {
            setFullname('');
            setImage(null)
            localStorage.removeItem('fullname');  // Clear fullname if user is null
            localStorage.removeItem('userImage');
        }
    };

    // Load user and fullname from localStorage on mount
    useEffect(() => {
        try {
            // Retrieve user and fullname from localStorage
            const storedUser = localStorage.getItem('user');
            const storedFullname = localStorage.getItem('fullname');
            const storedImage = localStorage.getItem('userImage');
    
            if (storedUser) {
                const parsedUser: User = JSON.parse(storedUser);
                setUser(parsedUser);
                setFullname(storedFullname || `${parsedUser.first_name} ${parsedUser.last_name}`);
                setImage(storedImage || parsedUser.image);
                if(parsedUser?.is_tenant === 1){
                    setType('tenant')
                }
        
                if(parsedUser?.is_client === 1){
                    setType('client')
                }else{
                    setType('tenant')
                }
            
            }
        } catch (error) {
            console.error('Error loading user from localStorage:', error);
        }
    }, []);
    
    const contextValue: UserContextType = { user, fullname, updateUser, type, image };

    return (
        <UserContext.Provider value={contextValue}>
            {children}
        </UserContext.Provider>
    );
};

export default UserProvider;
